/**
 * Página pública Acerca de / About (ruta /about, contenido en español)
 * Ilustraciones en /public/images/marketing/ (no usar /public/about/: choca con la ruta SPA /about y nginx devuelve 403).
 * @module pages/AboutPage
 */

import { Link } from 'react-router-dom'
import {
  Network,
  Map,
  Cable,
  Box,
  GitBranch,
  Server,
  FolderTree,
  Users,
  FileUp,
  Sparkles,
  Route,
  Target,
  Zap,
  MessageCircle,
  ArrowRight,
  CheckCircle2,
  Activity,
  LayoutGrid,
  Layers,
  Crosshair,
  ShieldCheck,
} from 'lucide-react'

const ABOUT_IMAGES = {
  hero: '/images/marketing/about-hero.png',
  map: '/images/marketing/about-map.png',
  trace: '/images/marketing/about-trace.png',
  nap: '/images/marketing/about-nap.png',
} as const

const features = [
  {
    icon: Map,
    title: 'Mapa interactivo',
    text: 'Diseña y visualiza la red sobre mapa (Leaflet): nodos, trazados de cable y contexto geográfico. Si varios puntos coinciden en el mismo lugar, puedes separarlos en abanico para elegir el nodo correcto.',
  },
  {
    icon: Activity,
    title: 'Trazabilidad de fibras',
    text: 'Sigue la ruta óptica a través de cables y equipos: desde la troncal o el PON hasta el cliente. Detecta incoherencias en la cadena y apoya el diagnóstico con la vista de traza en el mapa.',
  },
  {
    icon: LayoutGrid,
    title: 'Control de puertos y clientes',
    text: 'En cada NAP documentas puertos de usuario, estados y conexión a splitters o fibras. Asocia clientes, drops y rutas para saber qué hilo llega a quién sin perder el hilo conductor del diseño.',
  },
  {
    icon: Layers,
    title: 'Fibra por fibra, hilo por hilo',
    text: 'Estructura de cables con buffers y colores TIA-598, fibras individuales con estado (libre, ocupada, reservada) y empalmes. El diseño refleja el cable real, no solo una línea en el mapa.',
  },
  {
    icon: Box,
    title: 'Tipos de nodo',
    text: 'POP, OLT, NAP, cierres, clientes y postes: el inventario sigue el lenguaje del despliegue FTTH/FTTX. Al importar desde KML/KMZ puedes fijar el tipo de marcador (por ejemplo postes) o cambiar tipo en bloque cuando el nodo aún no tiene cables ni configuración avanzada.',
  },
  {
    icon: Cable,
    title: 'Cables y topología',
    text: 'Troncal, distribución, drop y acometida; rutas georreferenciadas y vínculo explícito entre nodos origen y destino.',
  },
  {
    icon: Server,
    title: 'POP y OLT',
    text: 'Configura equipos, slots y puertos PON enlazados a fibras del cable: el origen óptico queda trazable hasta el terreno.',
  },
  {
    icon: GitBranch,
    title: 'NAP, splitters y cascada',
    text: 'Splitters simples o en cascada, salidas hacia cables o hacia otros splitters, y puertos internos de la caja alineados con la lógica de la red. Editor de conexiones y sincronización coherente entre padre e hijo.',
  },
  {
    icon: Crosshair,
    title: 'Presupuesto óptico en la ruta',
    text: 'Estimación de pérdidas en el camino que atraviesa tu diseño (splitters y tramos relevantes), coherente con la topología que modelas.',
  },
  {
    icon: FolderTree,
    title: 'Proyectos y carpetas',
    text: 'Organiza redes por carpetas y proyectos para equipos que gestionan varias zonas o clientes.',
  },
  {
    icon: FileUp,
    title: 'Importación y exportación',
    text: 'KML y KMZ (compatible con Google Earth: puntos, líneas y polígonos como referencia), CSV, Excel e informes según plan. Importación pensada para marcadores reales de campo y documentación para oficina.',
  },
  {
    icon: Sparkles,
    title: 'Asistente de red (Pro)',
    text: 'Sugerencias asistidas por IA donde esté configurado, para acelerar tareas sobre la topología.',
  },
  {
    icon: Users,
    title: 'Equipos, organización y permisos',
    text: 'Multi-tenant, usuarios con roles y planes (incl. prueba Pro) para que comercial, ingeniería y campo compartan un único modelo con control de acceso.',
  },
]

const steps = [
  {
    n: '1',
    title: 'Crea tu espacio',
    text: 'Regístrate, define tu organización y abre carpetas o proyectos para cada red o despliegue.',
  },
  {
    n: '2',
    title: 'Dibuja en el mapa',
    text: 'Coloca nodos, traza cables y conecta origen y destino. La topología queda georreferenciada.',
  },
  {
    n: '3',
    title: 'Configura equipos, hilos y puertos',
    text: 'Detalla OLT/PON, NAP con splitters y cascada, cada fibra del cable y los puertos de cliente: de la troncal hasta el drop. Usa la trazabilidad para validar que la ruta del diseño es coherente.',
  },
  {
    n: '4',
    title: 'Exporta y comparte',
    text: 'Genera salidas para informes o campo (según tu plan) y mantén un único mapa de verdad del despliegue.',
  },
]

const trustPills = ['Trazabilidad de extremo a extremo', 'Puertos NAP y clientes', 'TIA-598 hilo a hilo', 'Multi-equipo y permisos']

function SectionImage({
  src,
  alt,
  priority,
}: {
  src: string
  alt: string
  priority?: boolean
}) {
  return (
    <div className="relative rounded-2xl md:rounded-3xl overflow-hidden border border-white/15 bg-slate-900/40 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.55)] ring-1 ring-white/5">
      <div className="absolute inset-0 bg-gradient-to-tr from-primary-900/30 via-transparent to-cyan-500/10 pointer-events-none z-[1]" aria-hidden />
      <img
        src={src}
        alt={alt}
        width={1200}
        height={800}
        className="w-full h-auto object-cover relative z-0"
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
      />
    </div>
  )
}

export function AboutPage() {
  return (
    <div className="min-h-screen bg-[#070d1a] text-white selection:bg-cyan-500/30 selection:text-white">
      {/* Fondo en capas: profundidad sin competir con el contenido */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900 via-[#0c1426] to-slate-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_55%_at_50%_-15%,rgba(56,189,248,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_100%_40%,rgba(59,130,246,0.08),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.035] bg-[linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[size:48px_48px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <nav className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-slate-300">
          <Link
            to="/"
            className="hover:text-white transition-colors inline-flex items-center gap-1.5 rounded-lg px-2 py-1 -ml-2 hover:bg-white/5"
          >
            ← Iniciar sesión
          </Link>
          <span className="text-slate-600">·</span>
          <Link to="/pricing" className="hover:text-white transition-colors rounded-lg px-2 py-1 hover:bg-white/5">
            Planes
          </Link>
          <span className="text-slate-600">·</span>
          <Link to="/soporte" className="hover:text-white transition-colors rounded-lg px-2 py-1 hover:bg-white/5">
            Soporte
          </Link>
        </nav>

        {/* Hero: patrón landing — eyebrow, titular, prueba social, imagen hero */}
        <header className="mt-10 md:mt-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          <div className="text-center lg:text-left order-2 lg:order-1">
            <p className="inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200/90 mb-5">
              Software para redes FTTH / FTTX
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-white leading-[1.08]">
              NetEasyFTTX
            </h1>
            <p className="mt-4 text-xl sm:text-2xl text-slate-200 font-medium max-w-xl mx-auto lg:mx-0 leading-snug">
              Gestión y diseño profesional de fibra óptica sobre mapa, con detalle hasta cada hilo.
            </p>
            <p className="mt-5 text-slate-400 max-w-xl mx-auto lg:mx-0 text-base leading-relaxed">
              Modela tu red con{' '}
              <span className="text-slate-200 font-medium">trazabilidad óptica</span>,{' '}
              <span className="text-slate-200 font-medium">control de puertos en NAP</span> y relación clara con{' '}
              <span className="text-slate-200 font-medium">clientes</span>. Un único entorno para ingeniería, operaciones y
              documentación alineada con campo.
            </p>

            <div className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-primary-500 to-cyan-500 text-white font-semibold shadow-lg shadow-primary-900/40 hover:brightness-110 active:scale-[0.98] transition-all"
              >
                Entrar a la aplicación
                <ArrowRight className="w-4 h-4" aria-hidden />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/20 text-white font-medium bg-white/5 hover:bg-white/10 hover:border-white/30 transition-colors"
              >
                Ver planes y prueba
              </Link>
            </div>

            <p className="mt-4 text-xs text-slate-500 flex flex-wrap items-center justify-center lg:justify-start gap-x-2 gap-y-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400/80 shrink-0" aria-hidden />
              <span>Prueba ampliada 21 días · Plan gratuito disponible después</span>
            </p>

            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-2">
              {trustPills.map((label) => (
                <span
                  key={label}
                  className="text-xs font-medium text-slate-300 bg-white/[0.06] border border-white/10 rounded-lg px-3 py-1.5"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="order-1 lg:order-2 max-w-lg mx-auto lg:max-w-none w-full">
            <SectionImage
              src={ABOUT_IMAGES.hero}
              alt="Ilustración abstracta de red de fibra óptica con nodos conectados"
              priority
            />
          </div>
        </header>

        {/* Qué es — tarjeta elevada con borde luminoso */}
        <section className="mt-20 md:mt-28" aria-labelledby="about-what">
          <h2 id="about-what" className="text-2xl md:text-3xl font-bold text-white mb-6 text-center lg:text-left">
            Qué es NetEasyFTTX
          </h2>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-6 md:p-10 shadow-2xl shadow-black/30 backdrop-blur-sm">
            <p className="text-slate-200 leading-relaxed text-base md:text-lg">
              NetEasyFTTX es un <strong className="text-white">sistema de gestión y diseño</strong> pensado para operadores,
              integradores y equipos de campo que despliegan fibra hasta el hogar o la empresa. Centraliza la{' '}
              <strong className="text-white">topología</strong> (quién está conectado a quién en el mapa), la{' '}
              <strong className="text-white">estructura de fibras</strong> (buffers, colores TIA-598, estado hilo a hilo) y
              la configuración lógica de <strong className="text-white">OLT, NAP, splitters y puertos de cliente</strong>.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              La <strong className="text-slate-200">trazabilidad</strong> te permite seguir el camino de la señal a través de
              los cables y equipos modelados, alineado con el presupuesto óptico en la ruta. El{' '}
              <strong className="text-slate-200">control de puertos</strong> en la NAP refleja qué puerto atiende a qué
              cliente o reserva, y cómo se enlaza con splitters y fibras de distribución o drop.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              No sustituye al EMS o NMS del fabricante de la OLT; <strong className="text-slate-200">complementa</strong>{' '}
              el trabajo de ingeniería y documentación con un modelo visual y exportable que todo el equipo puede entender.
            </p>
          </div>
        </section>

        {/* Bloques imagen + texto (zigzag) — storytelling visual */}
        <section className="mt-20 md:mt-28 space-y-16 md:space-y-24" aria-labelledby="about-visual">
          <h2 id="about-visual" className="sr-only">
            Funcionalidades destacadas en imágenes
          </h2>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <SectionImage
              src={ABOUT_IMAGES.map}
              alt="Vista de mapa con rutas de fibra y nodos de red"
            />
            <div>
              <p className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-2">Contexto geográfico</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Diseño donde ocurre la red</h3>
              <p className="text-slate-400 leading-relaxed">
                El mapa no es decoración: es el lienzo donde viven troncal, distribución y drops. Nodos solapados se pueden
                separar en abanico para trabajar con precisión en postes y cierres densos.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="lg:order-2">
              <SectionImage
                src={ABOUT_IMAGES.trace}
                alt="Representación de trazabilidad de fibra a lo largo de la red"
              />
            </div>
            <div className="lg:order-1">
              <p className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-2">Trazabilidad</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Del PON al cliente, sin perder el hilo</h3>
              <p className="text-slate-400 leading-relaxed">
                Recorre la cadena modelada en la herramienta: cables, empalmes, splitters y equipos. Valida coherencia antes
                de ir a campo y apoya diagnósticos con la vista de traza sobre el mapa.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <SectionImage
              src={ABOUT_IMAGES.nap}
              alt="Ilustración de panel de puertos de distribución de fibra tipo NAP"
            />
            <div>
              <p className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-2">Operación en NAP</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Puertos, estados y clientes</h3>
              <p className="text-slate-400 leading-relaxed">
                Documenta cada puerto con su uso real: libre, ocupado o reservado, vinculado a cliente y ruta óptica. Ese
                inventario técnico reduce idas en vano y acelera ampliaciones y soporte.
              </p>
            </div>
          </div>
        </section>

        {/* Trazabilidad — tarjetas con acento superior */}
        <section className="mt-20 md:mt-28" aria-labelledby="about-fine">
          <h2 id="about-fine" className="text-2xl md:text-3xl font-bold text-white mb-3 text-center lg:text-left">
            Trazabilidad y diseño hilo por hilo
          </h2>
          <p className="text-slate-400 mb-10 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
            Tres pilares que diferencian un dibujo genérico de un modelo listo para operar.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                Icon: Activity,
                accent: 'from-sky-400 to-cyan-400',
                title: 'Ruta óptica en el modelo',
                body: 'Recorre la cadena desde equipos y cables que has definido: la herramienta te ayuda a ver si la secuencia de fibras y splitters encaja con el trazado y a localizar huecos antes de ir a campo.',
              },
              {
                Icon: LayoutGrid,
                accent: 'from-emerald-400 to-teal-400',
                title: 'Puertos y clientes',
                body: 'Cada puerto de la NAP puede documentarse con estado y vínculo a cliente, fibra o cable drop. Así mantienes inventario útil para soporte, facturación técnica y ampliaciones.',
              },
              {
                Icon: Layers,
                accent: 'from-violet-400 to-fuchsia-400',
                title: 'Cable como objeto físico',
                body: 'No basta con una polilínea: modelas tubo, buffers y fibras con color y uso. Ese detalle es la base para empalmes, derivaciones y trazas correctas en redes densas.',
              },
            ].map(({ Icon, accent, title, body }) => (
              <div
                key={title}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-7 overflow-hidden hover:border-white/20 transition-colors"
              >
                <div
                  className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${accent} opacity-90`}
                  aria-hidden
                />
                <Icon className="w-9 h-9 text-white/90 mb-4" strokeWidth={1.75} aria-hidden />
                <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Para quién */}
        <section className="mt-20 md:mt-28" aria-labelledby="about-audience">
          <h2 id="about-audience" className="text-2xl md:text-3xl font-bold text-white mb-10 text-center lg:text-left">
            Para quién está hecho
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-amber-500/10 via-white/[0.04] to-transparent p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-8 h-8 text-amber-300 shrink-0" aria-hidden />
                <h3 className="text-xl font-semibold text-white">Empresa e ISP</h3>
              </div>
              <ul className="space-y-3 text-slate-300 text-sm md:text-base">
                <li className="flex gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
                  Visibilidad del despliegue por zona, proyecto o cliente.
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
                  Estándar único de documentación entre oficina y campo.
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
                  Escalado por equipos, permisos y planes (incl. prueba de 21 días con capacidades Pro).
                </li>
              </ul>
            </div>
            <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-sky-500/10 via-white/[0.04] to-transparent p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Route className="w-8 h-8 text-sky-300 shrink-0" aria-hidden />
                <h3 className="text-xl font-semibold text-white">Diseño y despliegue en fibra</h3>
              </div>
              <ul className="space-y-3 text-slate-300 text-sm md:text-base">
                <li className="flex gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
                  Trazado de cables y fibras con criterio TIA-598, estados por hilo y trazabilidad de la ruta óptica.
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
                  Configuración de PON, NAP, splitters en cascada y puertos de cliente alineada al despliegue real.
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
                  Importación/exportación (KML, CSV, Excel, informes) en plan Pro para cerrar el ciclo con GIS y oficina.
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Características — grid con hover sutil */}
        <section className="mt-20 md:mt-28" aria-labelledby="about-features">
          <h2 id="about-features" className="text-2xl md:text-3xl font-bold text-white mb-2 text-center lg:text-left">
            Características principales
          </h2>
          <p className="text-slate-400 mb-10 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
            Todo lo que puedes esperar al trabajar día a día con la plataforma.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 auto-rows-fr">
            {features.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 md:p-6 hover:border-cyan-500/25 hover:bg-white/[0.06] hover:-translate-y-0.5 transition-all duration-200 shadow-sm shadow-black/20"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/20 text-primary-200 mb-3">
                  <Icon className="w-5 h-5" strokeWidth={1.75} aria-hidden />
                </div>
                <h3 className="font-semibold text-white mb-2">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Cómo funciona */}
        <section className="mt-20 md:mt-28" aria-labelledby="about-steps">
          <h2 id="about-steps" className="text-2xl md:text-3xl font-bold text-white mb-10 text-center lg:text-left">
            Cómo funciona
          </h2>
          <ol className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-6">
            {steps.map((s) => (
              <li
                key={s.n}
                className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6 hover:bg-white/[0.05] transition-colors"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500/40 to-cyan-500/30 text-lg font-bold text-white border border-white/10">
                  {s.n}
                </span>
                <div>
                  <h3 className="font-semibold text-white text-lg mb-1">{s.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Planes — contraste claro tipo “pricing teaser” */}
        <section className="mt-20 md:mt-28 rounded-3xl border border-slate-200/10 bg-gradient-to-br from-slate-50 to-white text-slate-900 p-8 md:p-12 shadow-2xl shadow-black/40">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-slate-900">Planes flexibles</h2>
          <p className="text-slate-600 leading-relaxed mb-8 max-w-2xl">
            Empieza con <strong className="text-slate-900">21 días de prueba</strong> con capacidades ampliadas. Después
            puedes continuar en el plan <strong className="text-slate-900">gratuito básico</strong> (límites de nodos,
            cables y usuarios) o pasar a <strong className="text-slate-900">Pro</strong> para importación/exportación
            masiva, más colaboración y asistente IA donde esté disponible. La facturación Pro puede gestionarse desde la
            aplicación (Stripe).
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 font-semibold text-primary-700 hover:text-primary-900 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Ver comparativa de planes
            <ArrowRight className="w-4 h-4" aria-hidden />
          </Link>
        </section>

        {/* Diferenciadores */}
        <section className="mt-20 md:mt-28" aria-labelledby="about-why">
          <h2 id="about-why" className="text-2xl md:text-3xl font-bold text-white mb-10 text-center lg:text-left">
            Por qué NetEasyFTTX
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="text-center md:text-left p-6 rounded-2xl border border-white/10 bg-white/[0.03]">
              <Zap className="w-10 h-10 text-amber-300 mx-auto md:mx-0 mb-3" aria-hidden />
              <h3 className="font-semibold text-white mb-2">FTTX de punta a punta</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Mapa, topología, trazabilidad, puertos en NAP y detalle de fibras en un solo flujo, pensado para redes de
                acceso en fibra.
              </p>
            </div>
            <div className="text-center md:text-left p-6 rounded-2xl border border-white/10 bg-white/[0.03]">
              <Network className="w-10 h-10 text-cyan-300 mx-auto md:mx-0 mb-3" aria-hidden />
              <h3 className="font-semibold text-white mb-2">Producto en evolución</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                El servicio sigue mejorando con nuevas funciones y refinamiento según el uso real en despliegues.
              </p>
            </div>
            <div className="text-center md:text-left p-6 rounded-2xl border border-white/10 bg-white/[0.03]">
              <Map className="w-10 h-10 text-violet-300 mx-auto md:mx-0 mb-3" aria-hidden />
              <h3 className="font-semibold text-white mb-2">Alineado al trabajo de campo</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Conceptos que ya usas (NAP, OLT, troncal, drop) sin perder la vista del mapa y del inventario.
              </p>
            </div>
          </div>
        </section>

        {/* Feedback */}
        <section className="mt-20 md:mt-28 mb-10 rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-primary-950/80 to-slate-950/90 p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="flex gap-4">
              <MessageCircle className="w-12 h-12 text-cyan-300 shrink-0" strokeWidth={1.5} aria-hidden />
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Tu opinión construye el producto</h2>
                <p className="text-slate-400 leading-relaxed max-w-xl">
                  Queremos seguir mejorando NetEasyFTTX contigo. Si tienes ideas, necesidades de campo o sugerencias de
                  funcionalidad, escríbenos desde la página de soporte: leemos el feedback para priorizar el roadmap.
                </p>
              </div>
            </div>
            <Link
              to="/soporte"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold whitespace-nowrap transition-colors shadow-lg shadow-cyan-900/30"
            >
              Ir a soporte y feedback
              <ArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>
        </section>

        <footer className="pt-8 border-t border-white/10 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} NetEasyFTTX</p>
        </footer>
      </div>
    </div>
  )
}
